import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";

/**
 * Premium splash screen — shown on first visit each session.
 * Black bg, centered logo, elegant fade + scale + soft glow + fade-out.
 * Total duration ~2.4s, then unmounts.
 */
export default function SplashScreen({ onDone }) {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShow(false), 2400);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence onExitComplete={onDone}>
      {show && (
        <motion.div
          key="splash"
          data-testid="splash-screen"
          className="fixed inset-0 z-[100] grid place-items-center"
          style={{ background: "#000" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] } }}
        >
          {/* Soft radial glow behind logo */}
          <motion.div
            aria-hidden
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            style={{
              background: "radial-gradient(ellipse at 50% 45%, rgba(255,255,255,0.10), rgba(0,0,0,0) 55%)",
            }}
          />

          {/* Logo reveal — filter invert to make it white on black */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.85, filter: "blur(14px)" }}
            animate={{
              opacity: 1,
              scale: 1,
              filter: "blur(0px)",
              y: [0, -4, 0],
            }}
            transition={{
              opacity: { duration: 1.2, ease: [0.19, 1, 0.22, 1] },
              scale:   { duration: 1.4, ease: [0.19, 1, 0.22, 1] },
              filter:  { duration: 1.2 },
              y:       { duration: 3, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <Logo variant="full" invert className="w-[240px] sm:w-[300px] md:w-[340px] h-auto" loading="eager" />
          </motion.div>

          {/* Elegant loading line */}
          <motion.div
            aria-hidden
            className="absolute bottom-[18%] left-1/2 -translate-x-1/2 h-px w-40 overflow-hidden"
            style={{ background: "rgba(255,255,255,0.12)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <motion.div
              className="h-full bg-white"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ delay: 0.5, duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
